import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CreateOrEditUserComponent } from '../create-or-edit-user/create-or-edit-user.component';
import { FetchProjectMembersListRequest, DeleteUserRequest } from '../../../../../../../core/projects.actions';
import { projectDetails, projectMembersListInfo } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { AuthService } from '../../../../../../../../../shared/services';
import { Ability, subject } from '@casl/ability';

@Component({
    selector: 'app-account-management',
    templateUrl: './account-management.component.html',
    styleUrls: ['./account-management.component.scss'],
    standalone: false
})
export class AccountManagementComponent implements OnInit, OnDestroy {
  projectId: string;

  displayedColumns: string[] = ['name', 'email', 'phone', 'roleName', 'actions'];
  isDwrReportingHierarchyEnabled = false;
  isOrgAdmin = false;
  // Org-level roles this user may add/edit at project level (null = all).
  manageableOrgRoles: string[] | null = [];
  canManageProjectMembers = false;
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  selectedRow: any = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private store: Store,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private authService: AuthService,
    private ability: Ability
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.setOrgAdminState();
      this.fetchProjectMembersList();
    }

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (!data || Object.keys(data).length === 0) {
          return;
        }

        this.isDwrReportingHierarchyEnabled =
          data?.source_entity_extra_attributes?.dwr_reporting_hierarchy_enabled === true;
        this.canManageProjectMembers = this.ability.can(
          'manage-members',
          subject('Project', {
            id: data?.id || this.projectId,
            uuid: data?.uuid || this.projectId,
          })
        );
        this.updateDisplayedColumns();
      });

    this.store
      .select(projectMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;
            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.totalRecords = 0;
            this.dataSource = [];
          }
        }
      });
  }

  fetchProjectMembersList(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchProjectMembersListRequest({ payload }));
    }
  }

  setOrgAdminState(): void {
    const account = this.authService?.getCurrentUser();
    const roles = account?.roles || [];
    const projectRole = this.projectId && roles.length > 0
      ? roles.find((role: { project_id?: string }) => role.project_id === this.projectId)?.role
      : undefined;
    const effectiveRole = projectRole ?? account?.role;
    this.isOrgAdmin = effectiveRole === 'org-admin';
    this.manageableOrgRoles = this.isOrgAdmin ? null : account?.role === 'org-support' ? ['org-support'] : [];
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditUserComponent,
      data: {
        ...row,
        projectId: this.projectId,
        dwrReportingHierarchyEnabled: this.isDwrReportingHierarchyEnabled,
        isOrgAdmin: this.isOrgAdmin,
        manageableOrgRoles: this.manageableOrgRoles,
        readOnly: !this.canEditMember(row),
      },
    });
  }

  create(): void {
    if (!this.canManageProjectMembers) {
      return;
    }

    this.projectsService.openRightPanel.next({
      component: CreateOrEditUserComponent,
      data: {
        projectId: this.projectId,
        dwrReportingHierarchyEnabled: this.isDwrReportingHierarchyEnabled,
        isOrgAdmin: this.isOrgAdmin,
        manageableOrgRoles: this.manageableOrgRoles,
      },
    });
  }

  updateDisplayedColumns(): void {
    this.displayedColumns = this.isDwrReportingHierarchyEnabled
      ? ['name', 'email', 'phone', 'roleName', 'reportsTo', 'actions']
      : ['name', 'email', 'phone', 'roleName', 'actions'];
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  deleteUser(row: any, event: Event): void {
    event.stopPropagation();
    if (!this.canEditMember(row)) {
      return;
    }

    const userName = row?.name || (row?.first_name && row?.last_name ? `${row?.first_name} ${row?.last_name}` : 'this user');
    const message = `Are you sure you want to remove ${userName}?`;
    const dialogData = new ConfirmDialogModel('Remove?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          projectId: this.projectId,
          accountId: row?.id || row?.uuid,
        };
        this.store.dispatch(DeleteUserRequest({ payload }));
      }
    });
  }

  getReportsToName(row: any): string {
    const reportsTo = row?.reports_to;
    if (!reportsTo) {
      return '-';
    }

    return [reportsTo.first_name, reportsTo.last_name].filter(Boolean).join(' ') || '-';
  }

  isOrgLevelAccount(row: any): boolean {
    const role = row?.account_role || row?.accountRole;
    return typeof role === 'string' && role.startsWith('org-');
  }

  canManageOrgLevelAccount(row: any): boolean {
    if (!this.isOrgLevelAccount(row)) return true;
    const role = row?.account_role || row?.accountRole;
    return this.manageableOrgRoles === null || this.manageableOrgRoles.includes(role);
  }

  canEditMember(row: any): boolean {
    return this.canManageProjectMembers && this.canManageOrgLevelAccount(row);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
